import React, { ChangeEvent, FC, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import Styled from 'styled-components';

import { Alert, Box, BoxProps, Button, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import UserSearchWidget from '~/components/Common/UserSearchWidget';

import { FRAUD_STATUS, FRAUD_STATUS_LABELS, MANUAL_FRAUD_STATUS_OPTIONS, OrderPatchOperations } from '~/consts/order';
import stores from '~/consts/stores';

import ErrorDisplay from '../ErrorDisplay';
import PermissionedComponent from '../PermissionedComponent';

interface OrderFormProps {
  readonly isLoading: boolean;
}

const OrderFormContainer = Styled((props: BoxProps & OrderFormProps) => <Box component="div" {...props} />)`
  visibility: ${(props) => (props.isLoading ? 'hidden' : 'visible')}
` as FC<BoxProps & OrderFormProps>;

const SectionTitleContainer = Styled.div`
    margin-top: 5px;
    font-size: 18px;
    font-weight: 500;
`;

const SectionContainer = Styled.div`
    margin-top: 10px;
`;

const ButtonContainer = Styled((props: BoxProps) => <Box component="div" {...props} />)`
    margin-top: 20px;
` as FC<BoxProps>;

interface Props {
  order: App.Order;
  isDirty: boolean;
  isUpdating: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement>, op: OrderPatchOperations) => void;
  handleChangePurchaser: (op: OrderPatchOperations, purchaserId: string) => void;
  purchaser: App.User;
  isLoading: boolean;
  tenant: App.Tenant;
  onSave: () => void;
  error;
}

function OrderForm({
  isUpdating,
  purchaser,
  isDirty,
  error,
  isLoading,
  order,
  handleChange,
  handleChangePurchaser,
  onSave,
}: Props) {
  const [utmMedium, setUtmMedium] = useState(order.utm_medium);
  const [fraudStatus, setFraudStatus] = useState(order.fraud_status);

  const allowedUtmMediumMenuItems = ['admin', stores.chadstone_store.utmMedium, stores.bondi_store.utmMedium];

  if (order.utm_medium && !allowedUtmMediumMenuItems.includes(order.utm_medium)) {
    allowedUtmMediumMenuItems.unshift(order.utm_medium);
  }

  const isCurrentStatusAuto = order.fraud_status?.startsWith('auto_');

  const onHandleChangePurchaser = (id: string, purchaserId: string) => {
    handleChangePurchaser(OrderPatchOperations.PATCH_REPLACE_ORDER, purchaserId);
  };

  const onHandleChangeUtmMedium = (e) => {
    setUtmMedium(e.target.value);
    handleChange(e, OrderPatchOperations.PATCH_REPLACE_ORDER);
  };

  const onHandleFraudStatusChange = (e) => {
    setFraudStatus(e.target.value);
    handleChange(e, OrderPatchOperations.PATCH_UPDATE_FRAUD_STATUS);
  };

  // Check for fraud status changes and show warning message
  const warningMessage = useMemo(() => {
    const originalStatus = order.fraud_status;
    const currentStatus = fraudStatus;

    const isChangingToFraud =
      originalStatus !== FRAUD_STATUS.MANUAL_REVIEWED_FRAUD &&
      originalStatus !== FRAUD_STATUS.AUTO_REVIEWED_FRAUD &&
      currentStatus === FRAUD_STATUS.MANUAL_REVIEWED_FRAUD;

    const isChangingFromFraud =
      (originalStatus === FRAUD_STATUS.MANUAL_REVIEWED_FRAUD || originalStatus === FRAUD_STATUS.AUTO_REVIEWED_FRAUD) &&
      currentStatus === FRAUD_STATUS.MANUAL_REVIEWED_NOT_FRAUD;

    if (isChangingToFraud) {
      return 'Be mindful, the change to fraud status will result in loyalty points awarding being reversed';
    } else if (isChangingFromFraud) {
      return 'Be mindful, the change from fraud status will result in loyalty points awarding being re-awarded';
    }
    return null;
  }, [fraudStatus, order.fraud_status]);

  return (
    <OrderFormContainer isLoading={isLoading}>
      <Helmet>
        <title>Orders | {purchaser.fullName} | Edit Order</title>
      </Helmet>
      <SectionTitleContainer>Sold By: {purchaser && <strong>{purchaser.fullName}</strong>}</SectionTitleContainer>
      <PermissionedComponent>
        <SectionContainer>
          <UserSearchWidget
            id="fk_customer_id"
            label="Name / Email / Customer Support Code / User ID"
            formErrors={[]}
            loading={false}
            onChange={onHandleChangePurchaser}
          />
        </SectionContainer>
        <SectionTitleContainer>UTM Medium</SectionTitleContainer>
        <SectionContainer>
          <Select
            name="utm_medium"
            value={utmMedium}
            onChange={(e: SelectChangeEvent<string>) => onHandleChangeUtmMedium(e)}
          >
            {allowedUtmMediumMenuItems.map((utmMediumValue) => (
              <MenuItem key={utmMediumValue} value={utmMediumValue}>
                {utmMediumValue}
              </MenuItem>
            ))}
          </Select>
        </SectionContainer>
      </PermissionedComponent>

      {/* Fraud should be allowed for all employees, not just employees */}
      <SectionTitleContainer>Fraud Status</SectionTitleContainer>
      <SectionContainer>
        <Select
          name="fraudStatus"
          value={fraudStatus}
          onChange={(e: SelectChangeEvent<string>) => onHandleFraudStatusChange(e)}
        >
          {/* Show current status if it's auto-generated (as disabled option) */}
          {isCurrentStatusAuto && (
            <MenuItem value={order.fraud_status} disabled>
              {FRAUD_STATUS_LABELS[order.fraud_status] || order.fraud_status}
            </MenuItem>
          )}

          {/* Always show manual options for selection */}
          {MANUAL_FRAUD_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </SectionContainer>

      {warningMessage && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning">{warningMessage}</Alert>
        </Box>
      )}
      {error ? <ErrorDisplay message={error.message} /> : null}

      <ButtonContainer className="button-container">
        <Button
          type="submit"
          onClick={isDirty && !isUpdating ? onSave : null}
          variant="contained"
          className={`${isDirty && !isUpdating ? null : 'disabled'}`}
          disabled={!(isDirty && !isUpdating)}
        >
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </ButtonContainer>
    </OrderFormContainer>
  );
}

export default OrderForm;
